import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { resolveLookbook, refetchManifest, derivativeThumbnailUrl, derivativeFullUrl, BACKEND_URL } from '@/lib/lookbook-api';
import { Lock, UserCheck, Download, Image, Video } from 'lucide-react';

export default function LookbookViewerPage() {
  const navigate = useNavigate();
  const { token } = useParams();
  const { user, status } = useAuth();

  const [manifest, setManifest] = useState(null);
  const [viewerToken, setViewerToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [needsAuth, setNeedsAuth] = useState(false);
  const [needsPassword, setNeedsPassword] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [errorCode, setErrorCode] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  const attemptResolve = useCallback(async (password) => {
    if (!token) return;
    setLoading(true); setNeedsAuth(false); setNeedsPassword(false); setErrorCode(null); setErrorMessage(null);
    const useAuthFlag = status === 'signed-in';
    try {
      const res = await resolveLookbook({ share_token: token, password }, useAuthFlag);
      setManifest(res.manifest); setViewerToken(res.viewer_token);
    } catch (e) {
      const code = e?.code || 'UNKNOWN';
      if (code === 'PASSWORD_REQUIRED') setNeedsPassword(true);
      else if (code === 'AUTH_REQUIRED') setNeedsAuth(true);
      else { setErrorCode(code); setErrorMessage(e?.message || 'Something went wrong.'); }
    } finally { setLoading(false); }
  }, [token, status]);

  useEffect(() => { if (status !== 'loading') attemptResolve(); }, [status, attemptResolve]);

  // Poll manifest
  useEffect(() => {
    if (!viewerToken) return;
    const iv = setInterval(async () => {
      try { const res = await refetchManifest(viewerToken); setManifest(res.manifest); }
      catch (e) { if (e?.code === 'LOOKBOOK_NOT_FOUND') { setManifest(null); setViewerToken(null); setErrorCode('LOOKBOOK_NOT_FOUND'); setErrorMessage('This lookbook is no longer available.'); } }
    }, 20000);
    return () => clearInterval(iv);
  }, [viewerToken]);

  // Phase 13a — client-side meta updates for human-facing polish
  useEffect(() => {
    if (!manifest) return;
    const title = manifest.title ? `${manifest.title} — ALLVIEWS Lookbook` : 'ALLVIEWS Lookbook';
    document.title = title;
    setMetaTag('og:title', title);
    setMetaTag('og:description', manifest.subtitle || 'Look what we captured — every guest, every angle.');
    if (token) setMetaTag('og:image', `${BACKEND_URL}/api/v1/lookbooks/${encodeURIComponent(token)}/og-image.png`);
    setMetaTag('twitter:card', 'summary_large_image');
    return () => { document.title = 'ALLVIEWS'; };
  }, [manifest, token]);

  const cobrand = manifest?.cobranding;
  const showCobrand = cobrand && cobrand.enabled && cobrand.display_name;

  return (
    <div className="max-w-3xl mx-auto px-4 py-8" data-testid="lookbook-viewer-screen">
      <div className="mb-4 flex items-center gap-2">
        <img src="/allviews-mark.svg" alt="" className="h-5 w-5" />
        <img src="/allviews-wordmark.svg" alt="ALLVIEWS" className="h-3.5" />
      </div>

      {loading ? (
        <div className="flex justify-center py-12" data-testid="lookbook-viewer-loading"><div className="w-8 h-8 border-2 border-[#146BFF] border-t-transparent rounded-full animate-spin" /></div>
      ) : errorCode ? (
        <div className="bg-[#FBE5E5] border border-[#C93434]/20 rounded-xl p-6" data-testid="lookbook-viewer-error">
          <h2 className="text-xl font-extrabold text-[#0B1B46]" data-testid="lookbook-viewer-error-title">This lookbook isn&apos;t available</h2>
          <p className="text-sm text-[#5B6472] mt-1" data-testid="lookbook-viewer-error-message">{errorMessage}</p>
          <p className="text-xs text-[#5B6472] mt-2" data-testid="lookbook-viewer-error-code">Code: {errorCode}</p>
        </div>
      ) : needsPassword ? (
        <div className="bg-white border border-[#E3E8EF] rounded-xl p-6" data-testid="lookbook-viewer-password-card">
          <Lock className="w-7 h-7 text-[#146BFF] mb-2" />
          <h2 className="text-xl font-extrabold text-[#0B1B46]">Password protected</h2>
          <p className="text-sm text-[#5B6472] mt-1 mb-4">Enter the password the host shared with you.</p>
          <input type="password" value={passwordInput} onChange={e => setPasswordInput(e.target.value)} placeholder="Password"
            className="w-full h-11 px-4 border border-[#E3E8EF] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#146BFF] bg-white mb-3" data-testid="lookbook-viewer-password-input" />
          <button onClick={() => attemptResolve(passwordInput)} disabled={passwordInput.length < 4}
            className="w-full h-11 bg-[#146BFF] hover:bg-[#0F55CC] disabled:opacity-50 text-white font-semibold rounded-lg transition-colors" data-testid="lookbook-viewer-unlock-btn">Unlock</button>
        </div>
      ) : needsAuth ? (
        <div className="bg-white border border-[#E3E8EF] rounded-xl p-6" data-testid="lookbook-viewer-auth-card">
          <UserCheck className="w-7 h-7 text-[#146BFF] mb-2" />
          <h2 className="text-xl font-extrabold text-[#0B1B46]">Sign in to view</h2>
          <p className="text-sm text-[#5B6472] mt-1 mb-4">This lookbook is restricted to event attendees.</p>
          <button onClick={() => { try { localStorage.setItem('allviews.pendingLookbookToken', token || ''); } catch (_e) { /* ignore */ } navigate('/sign-in'); }}
            className="w-full h-11 bg-[#146BFF] text-white font-semibold rounded-lg" data-testid="lookbook-viewer-signin-btn">Sign in</button>
        </div>
      ) : manifest ? (
        <div className="flex flex-col gap-4">
          {/* Hero */}
          {manifest.hero_submission_id && viewerToken ? (
            <img src={derivativeThumbnailUrl(manifest.hero_submission_id, viewerToken)} alt="" className="w-full h-[280px] rounded-xl object-cover bg-[#F3F5F8]" data-testid="lookbook-viewer-hero" />
          ) : (
            <div className="w-full h-[200px] rounded-xl bg-[#F3F5F8] flex items-center justify-center"><Image className="w-10 h-10 text-[#5B6472]" /></div>
          )}

          <h1 className="text-3xl font-extrabold text-[#0B1B46]" data-testid="lookbook-viewer-title">{manifest.title}</h1>
          {manifest.subtitle && <p className="text-sm text-[#5B6472]" data-testid="lookbook-viewer-subtitle">{manifest.subtitle}</p>}
          <div className="flex flex-wrap gap-2">
            <span className="px-2 py-0.5 text-xs font-bold bg-[#EFF5FF] text-[#146BFF] rounded-full">{manifest.photo_count} photos</span>
            <span className="px-2 py-0.5 text-xs font-bold bg-[#EFF5FF] text-[#146BFF] rounded-full">{manifest.video_count} videos</span>
            <span className="px-2 py-0.5 text-xs font-bold bg-[#EFF5FF] text-[#146BFF] rounded-full">{manifest.contributor_count} contributors</span>
            {manifest.state === 'PAUSED' && <span className="px-2 py-0.5 text-xs font-bold bg-[#FBEEDA] text-[#B86500] rounded-full">Paused</span>}
          </div>

          {manifest.intro_text && <div className="bg-white border border-[#E3E8EF] rounded-xl p-4 text-sm leading-relaxed" data-testid="lookbook-viewer-intro">{manifest.intro_text}</div>}

          {(manifest.sections || []).map(sec => (
            <div key={sec.id} className="flex flex-col gap-3" data-testid={`lookbook-viewer-section-${sec.id}`}>
              <h2 className="text-xl font-extrabold text-[#0B1B46] mt-2">{sec.title}</h2>
              <div className="flex flex-wrap gap-3">
                {(sec.items || []).map(it => {
                  const thumb = viewerToken ? derivativeThumbnailUrl(it.submission_id, viewerToken) : null;
                  const full = viewerToken ? derivativeFullUrl(it.submission_id, viewerToken) : null;
                  return (
                    <div key={it.submission_id} className="w-[156px]" data-testid={`lookbook-viewer-item-${it.submission_id}`}>
                      {thumb ? <img src={thumb} alt="" className="w-[156px] h-[156px] rounded-lg object-cover bg-[#F3F5F8]" /> : <div className="w-[156px] h-[156px] rounded-lg bg-[#F3F5F8] flex items-center justify-center">{it.media_kind === 'video' ? <Video className="w-5 h-5 text-[#5B6472]" /> : <Image className="w-5 h-5 text-[#5B6472]" />}</div>}
                      {manifest.show_contributor_names && it.contributor_first_name && <p className="text-[11px] font-bold text-[#146BFF] mt-1" data-testid={`lookbook-viewer-name-${it.submission_id}`}>&mdash; {it.contributor_first_name}</p>}
                      {it.caption && <p className="text-xs text-[#5B6472]">{it.caption}</p>}
                      {manifest.allow_downloads && full && (
                        <button onClick={() => window.open(full, '_blank')} className="flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded bg-[#EFF5FF] text-[#146BFF] text-[11px] font-bold" data-testid={`lookbook-viewer-download-${it.submission_id}`}><Download className="w-3 h-3" /> Download</button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          {manifest.closing_text && <div className="bg-white border border-[#E3E8EF] rounded-xl p-4 text-sm leading-relaxed" data-testid="lookbook-viewer-closing">{manifest.closing_text}</div>}

          {/* Phase 13b — Co-branding credit */}
          {showCobrand && (
            <div className="flex items-center justify-center gap-3 py-4 border-t border-[#E3E8EF] mt-2" data-testid="lookbook-viewer-cobrand">
              {cobrand.logo_url && (
                <img
                  src={cobrand.logo_url.startsWith('http') ? cobrand.logo_url : `${BACKEND_URL.replace('/api', '')}${cobrand.logo_url}`}
                  alt={cobrand.display_name}
                  className="h-7 w-7 rounded object-contain bg-white border border-[#E3E8EF]"
                  data-testid="lookbook-viewer-cobrand-logo"
                />
              )}
              <span className="text-xs text-[#5B6472]" data-testid="lookbook-viewer-cobrand-text">
                Captured by <span className="font-bold text-[#0B1B46]">{cobrand.display_name}</span>
                {cobrand.website && (
                  <> &middot; <a href={cobrand.website.startsWith('http') ? cobrand.website : `https://${cobrand.website}`} target="_blank" rel="noopener noreferrer" className="text-[#146BFF] hover:underline" data-testid="lookbook-viewer-cobrand-link">{cobrand.website.replace(/^https?:\/\//, '')}</a></>
                )}
              </span>
            </div>
          )}

          {manifest.watermark && (
            <div className="flex items-center justify-center gap-2 py-4 border-t border-[#E3E8EF] mt-2" data-testid="lookbook-viewer-watermark">
              <img src="/allviews-mark.svg" alt="" className="h-4 w-4" />
              <span className="text-xs font-bold text-[#5B6472]">Made with ALLVIEWS</span>
            </div>
          )}

          <p className="text-center text-[11px] text-[#5B6472] mt-6" data-testid="lookbook-viewer-footer">
            Delivered privately by ALLVIEWS. {user ? `Signed in as ${user.email}.` : 'Anonymous view.'}
          </p>
        </div>
      ) : null}
    </div>
  );
}

/** Update or create an og/meta tag in the document head */
function setMetaTag(property, content) {
  let el = document.querySelector(`meta[property="${property}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('property', property);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}
